'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { cn } from '@/components/atoms/primitives/utils';
import {
  GLOBAL_NAV_ACCENT_VARS,
  GN_ACTIVE_LINK_GLOW_STATIC,
  GlobalNavCoordStrip,
} from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import { WayfinderSheet } from '@/components/molecules/WayfinderSheet';
import { HamburgerDrawer } from '@/components/organisms/AppShell/HamburgerDrawer';
import { crossHostLogin } from '@/lib/cross-host-redirect';

interface NavItem {
  href: string;
  label: string;
}

/**
 * Apex layer nav items — source of truth for labels + paths:
 * `docs/design-system/1.1.0/templates/apex/index.html` (header nav row).
 * WEB-333 keeps this exact set — the DS 1.2.0 mock's link set differs
 * deliberately from our shipped routes.
 */
// Launch readiness: the apex For promoters / Developers / About routes render
// coming-soon stubs, so only the real `/cities` page is surfaced in the nav.
// Restore the other entries once those pages ship.
const APEX_NAV_ITEMS: ReadonlyArray<NavItem> = [{ href: '/cities', label: 'Cities' }];

// Apex header is the cross-city national surface — the Electric Amber accent
// rides through `--gn-accent*` (set inline via GLOBAL_NAV_ACCENT_VARS.apex).
// Focus ring reaches for `--loci-amber` directly so the apex surface stays
// amber-themed even though `--color-ring` flips to CLT Skyline elsewhere.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-loci-amber focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F141F]';

// WEB-333 — DS 1.2.0 `o-globalnav__link`: Space Grotesk 13px / 500, 8px 12px
// padding, `--r-md` radius, `--fg-2` → `--fg-1` + glass wash on hover, glowing
// amber underline on the `aria-current="page"` item.
const NAV_LINK_BASE =
  'relative inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center rounded-ds-md px-3 py-2 font-display text-small font-medium tracking-[0.005em] text-white/80 transition-colors duration-150 hover:bg-white/[0.06] hover:text-white motion-reduce:transition-none';

const NAV_LINK_ACTIVE = `text-white ${GN_ACTIVE_LINK_GLOW_STATIC}`;

export interface SiteHeaderApexViewProps {
  /** Live scroll state from {@link useScrolledPast} + connected `SiteHeader`. */
  isScrolled?: boolean;
  /** Overrides {@link isScrolled} for Storybook / visual regression (opaque bar). */
  forceScrolled?: boolean;
  /**
   * Request-host staging flag (WEB-776). Resolved by `SiteHeaderApexMount`
   * so this View stays prop-driven.
   */
  isStaging?: boolean;
  className?: string;
}

function isApexLinkActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
}

function ApexNavLink({ item, isActive }: { item: NavItem; isActive: boolean }) {
  return (
    <Link
      href={item.href}
      aria-current={isActive ? 'page' : undefined}
      className={cn(NAV_LINK_BASE, FOCUS_RING, isActive && NAV_LINK_ACTIVE)}
    >
      {item.label}
    </Link>
  );
}

/**
 * Apex global-nav header (WEB-333 repaint to DS 1.2.0 `o-globalnav`):
 *
 *  - Always-on 26px coord strip — `APEX · LOCI.CITY` eyebrow with the amber
 *    pulse dot, `— GLOBAL —` position, `EST. 2026` meta (no fabricated
 *    counts).
 *  - 56px bar — transparent over the hero, then `rgba(15,20,31,0.78)` +
 *    `blur(18px) saturate(1.4)` glass once scrolled.
 *  - Brand lettermark (mark unchanged) + Wayfinder layer switcher.
 *  - Nav at `≥900px` with the glowing amber underline on the active route;
 *    {@link HamburgerDrawer} below `900px`.
 *  - CTA: ghost "Sign in" (cross-host app login — wiring unchanged). The
 *    amber "Get listed" pill is hidden for launch readiness because its
 *    `/promoters` target is a coming-soon stub.
 */
export function SiteHeaderApexView({
  isScrolled = false,
  forceScrolled,
  isStaging,
  className = '',
}: SiteHeaderApexViewProps) {
  const scrolled = forceScrolled ?? isScrolled;
  const pathname = usePathname();

  // `force: true` appends the `?force=1` escape hatch so a returning visitor
  // whose cookie is expired-but-present isn't bounced off /login back to the
  // feed — consistent with the AppHeader and marketing SiteHeader Sign-in links.
  const signInHref = crossHostLogin(undefined, { force: true, isStaging });

  return (
    <header
      aria-label="Apex"
      style={GLOBAL_NAV_ACCENT_VARS.apex}
      className={cn('sticky top-0 z-50 w-full pt-[env(safe-area-inset-top)]', className)}
    >
      <GlobalNavCoordStrip
        eyebrow="APEX · LOCI.CITY"
        position="Global"
        meta={<span>EST. 2026</span>}
        testId="site-header-apex-coord-strip"
      />
      <div
        data-testid="site-header-apex-bar"
        className={cn(
          'transition-[background-color,backdrop-filter,border-color] duration-200 motion-reduce:transition-none',
          scrolled
            ? 'border-b border-white/[0.08] bg-[rgba(15,20,31,0.78)] backdrop-blur-[18px] backdrop-saturate-[1.4]'
            : 'border-b border-transparent bg-transparent',
        )}
      >
        <div className="relative mx-auto flex h-14 max-w-[1440px] items-center justify-between gap-3 px-4 sm:gap-4 sm:px-6">
          <div className="flex min-w-0 shrink-0 items-center gap-2">
            <Link
              href="/"
              aria-label="LOCI homepage"
              className={cn(
                'inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center',
                FOCUS_RING,
              )}
            >
              <Image
                src="/brand/loci-lettermark.svg"
                alt=""
                width={22}
                height={22}
                className="h-[22px] w-auto"
                priority
              />
            </Link>
            <WayfinderSheet layer="apex" />
          </div>

          <nav
            aria-label="Primary"
            className="absolute left-1/2 hidden min-h-0 min-w-0 max-w-[70vw] -translate-x-1/2 items-center justify-center gap-0.5 overflow-x-auto min-[900px]:flex sm:gap-1"
          >
            {APEX_NAV_ITEMS.map((item) => (
              <ApexNavLink
                key={item.href}
                item={item}
                isActive={isApexLinkActive(pathname, item.href)}
              />
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3 min-[900px]:ml-auto">
            <a
              href={signInHref}
              className={cn(
                'inline-flex h-9 min-h-[44px] desktop:min-h-0 items-center rounded-full border border-white/[0.16] bg-transparent px-3 font-display text-small font-semibold text-white/90 transition-colors duration-150 hover:border-white hover:text-white motion-reduce:transition-none sm:px-4',
                FOCUS_RING,
              )}
            >
              Sign in
            </a>

            {/*
             * Launch readiness: the "Get listed" CTA pointed at the apex
             * `/promoters` portal, which is a coming-soon stub. Hidden until a
             * real promoter onboarding route exists — re-add the amber pill
             * (amber-on-navy CLT pairing) once that page ships.
             */}

            <div className="min-[900px]:hidden">
              <HamburgerDrawer />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
